<script setup lang="ts">
import type { DownloadManagerProps } from '/#/components/base/downloads'
import type { TabItem } from '/#/components/base/tabs'
import type { ComputedRef, Ref, WritableComputedRef } from 'vue'

import groupBy from 'lodash-es/groupBy'

import Tabs from '/@/components/base/tabs/Tabs.vue'
import { FileDownloadStatusEnum } from '/@/enums/fileDownloadStatusEnum'

const { t } = useI18n()
const downloadStore = useDownloadsStore()

const accountStore = useAccountStore()

const props = withDefaults(defineProps<DownloadManagerProps>(), {
  title: 'Downloads',
})

const tabsElement = ref<InstanceType<typeof Tabs>>()

const isVisible: Ref<boolean> = ref(false)

const AUTO_CLOSE_DURATION = 8000 // 8 seconds
let timeoutId: ReturnType<typeof setTimeout> | null = null

/**
 * @name activeTab
 * @type {WritableComputedRef<string>}
 * @description Check if there is 'IN_PROGRESS' status in downloads.
 * If not, update tab value to 'COMPLETED'
 */
const activeTab: WritableComputedRef<string> = computed(() => {
  if (!props.modelValue.some((item) => item.status === 'IN_PROGRESS')) {
    tabsElement.value?.changeActiveTab('COMPLETED')
    return 'COMPLETED'
  }
  return 'IN_PROGRESS'
})

/**
 * @name tabs
 * @type {ComputedRef<TabItem[]>}
 * @description Tabs item for status value.
 */
const tabs: ComputedRef<TabItem[]> = computed(() => {
  const inProgress = {
    label: t('issues.in-progress'),
    value: FileDownloadStatusEnum.IN_PROGRESS,
    badge: props.modelValue.filter(
      (file) =>
        file.status === FileDownloadStatusEnum.IN_PROGRESS ||
        file.status === FileDownloadStatusEnum.FAILED,
    ).length,
  }

  const completed = {
    label: t('common.completed'),
    value: FileDownloadStatusEnum.COMPLETED,
    badge: props.modelValue.filter(
      (file) => file.status === FileDownloadStatusEnum.COMPLETED,
    ).length,
  }

  return [inProgress, completed]
})

/**
 * @name groupedDownloadsByStatus
 * @type {ComputedRef<any>}
 * @description For grouping files according to status.
 */
const groupedDownloadsByStatus: ComputedRef<any> = computed(() => {
  return groupBy(props.modelValue, 'status')
})

/**
 * @name shouldShowDownloadManager
 * @type {ComputedRef<boolean>}
 * @description Controls the visibility of the download manager based on component visibility and user login status.
 */
const shouldShowDownloadManager = computed(() => {
  return isVisible.value && accountStore.isLoggedIn
})

/**
 * @method resetAutoCloseTimer
 * @description Reset the auto-close timer when clicked.
 */
function resetAutoCloseTimer() {
  if (timeoutId) clearTimeout(timeoutId)
  timeoutId = setTimeout(() => {
    isVisible.value = false
  }, AUTO_CLOSE_DURATION)
}

/**
 * @method showDownloadManager
 * @description Visibility of download manager.
 */
function showDownloadManager() {
  isVisible.value = true
  resetAutoCloseTimer()
}

/**
 * @method close
 * @description Close download manager.
 */
async function close() {
  isVisible.value = false
  await nextTick()
  downloadStore.clearDownloads()
}

/**
 * @method pauseAutoCloseTimer
 * @description Pause the auto-close timer on hover.
 */
function pauseAutoCloseTimer() {
  if (timeoutId) clearTimeout(timeoutId)
}

/**
 * @method resumeAutoCloseTimer
 * @description Resume the auto-close timer after hover.
 */
function resumeAutoCloseTimer() {
  resetAutoCloseTimer()
}

watch(props.modelValue, (newDownloads) => {
  if (newDownloads.length > 0) {
    showDownloadManager()
  }
})

onUnmounted(() => {
  if (timeoutId) clearTimeout(timeoutId)
  window.removeEventListener('click', resetAutoCloseTimer)
})

defineExpose({ pauseAutoCloseTimer, resumeAutoCloseTimer })
</script>

<template>
  <Transition name="from-bottom" mode="out-in">
    <div
      v-if="shouldShowDownloadManager"
      class="downloads-wrapper"
     
      @mouseover="pauseAutoCloseTimer"
      @mouseleave="resumeAutoCloseTimer"
      @focusin="pauseAutoCloseTimer"
      @focusout="resumeAutoCloseTimer"
    >
      <Flex flex-direction="column">
        <div class="header">
          <Flex flex-direction="row" justify-content="space-between">
            <span>{{ title }}</span>
            <Button
              circle
              :icon="{ name: 'close', color: 'slate' }"
              variant="link"
              size="sm"
              color="slate"
             
              @click="close"
            />
          </Flex>
        </div>
        <Divider />
        <div class="switcher-tag">
          <Flex flex-direction="column">
            <Tabs ref="tabsElement" :tabs="tabs" :selected="activeTab">
              <template #tab="{ activeValue }">
                <Divider class="mx-3" />
                <div class="download-item">
                  <div v-if="activeValue === FileDownloadStatusEnum.IN_PROGRESS">
                    <div>
                      <DownloadManagerItem
                        v-for="download in groupedDownloadsByStatus[
                          FileDownloadStatusEnum.IN_PROGRESS
                        ]"
                        :key="download.id"
                        :file="download"
                      />
                      <DownloadManagerItem
                        v-for="download in groupedDownloadsByStatus[
                          FileDownloadStatusEnum.FAILED
                        ]"
                        :key="download.id"
                        :file="download"
                      />
                    </div>
                  </div>
                  <div v-if="activeValue === FileDownloadStatusEnum.COMPLETED">
                    <DownloadManagerItem
                      v-for="download in groupedDownloadsByStatus[
                        FileDownloadStatusEnum.COMPLETED
                      ]"
                      :key="download.id"
                      :file="download"
                    />
                  </div>
                </div>
              </template>
            </Tabs>
          </Flex>
        </div>
      </Flex>
    </div>
  </Transition>
</template>
